import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditCategory: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('categories');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    hsCode: "",
    description: "",
    id: id,
    tax: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  // Fetch existing item
  useEffect(() => {
    const fetchItem = async () => {
      try {
        const response = await adminApi.call(`/category/${id}`, 'GET');

        if (!response.success) {
          showErrorToast(response.error || t('messages.fetchError'));
          return;
        }

        setFormData(response.data);
      } catch (error: any) {
        showErrorToast(t('messages.fetchError') + ": " + error.message);
      }
    };

    fetchItem();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.name?.trim()) {
      newErrors.name = t('validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('validation.nameMinLength');
    }
    if (!formData.hsCode?.trim()) {
      newErrors.hsCode = t('validation.hsCodeRequired');
    }
    if (!formData.description?.trim()) {
      newErrors.description = t('validation.descriptionRequired');
    }
    if (!formData.tax || Number(formData.tax) < 0 || Number(formData.tax) > 100) {
      newErrors.tax = t('validation.taxRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t('validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/category/admin/Update', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));
    } catch (error: any) {
      showErrorToast(t('messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbs = [
    { label: t('breadcrumbs.categories'), path: '/categories' },
    { label: t('breadcrumbs.edit') }
  ];

  return (
    <PageLayout
      title={t('editCategory')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}
    >
      <div className="card shadow-lg border-0 rounded-3 p-4">
        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.name')} <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.name ? 'is-invalid' : ''}`}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('placeholders.name')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.hsCode')} <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.hsCode ? 'is-invalid' : ''}`}
              name="hsCode"
              value={formData.hsCode}
              onChange={handleInputChange}
              placeholder={t('placeholders.hsCode')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
            {errors.hsCode && <div className="invalid-feedback">{errors.hsCode}</div>}
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.description')} <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.description ? 'is-invalid' : ''}`}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('placeholders.description')}
              dir={isRTL ? 'rtl' : 'ltr'}
              required
            />
            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.tax')} <span className="required">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.tax ? 'is-invalid' : ''}`}
              name="tax"
              value={formData.tax}
              onChange={handleInputChange}
              placeholder={t('placeholders.tax')}
              dir={isRTL ? 'rtl' : 'ltr'}
              min="0"
              max="100"
              step="0.01"
              required
            />
            {errors.tax && <div className="invalid-feedback">{errors.tax}</div>}
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/categories')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={submitUpdate}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>
    </PageLayout>
  );
};
